import { useCallback } from 'react';

import {
  CounterpartyService,
  CounterpartyCreateUpdate,
  CounterpartyQueryFilters,
  CounterpartyApprovalPatch
} from 'openapi';

import { showSuccessToastMessage } from 'components/shared/Toast/Toast';

import { useTranslations } from 'context/TranslationContext';

import { useInterceptor } from 'hooks/useInterceptor';

export const useCounterPartyController = () => {
  const { translate } = useTranslations();
  const { interceptRequest } = useInterceptor();

  const {
    createCounterparty,
    updateCounterparty,
    deleteCounterpartiesByIds,
    getCounterpartiesByCompanyIdAndFilters,
    getCounterpartyFiltersByCompanyIdAndType,
    getCounterpartyApprovals,
    updateCounterpartyApprovalById,
    checkCounterpartyExist,
    getCounterparty
  } = CounterpartyService;

  const createNewCounterParty = (id: number, body: CounterpartyCreateUpdate) =>
    interceptRequest(
      createCounterparty,
      {
        onSuccess: () =>
          showSuccessToastMessage(translate('messages.successfullyCreated'))
      },
      id,
      body
    );

  const editCounterParty = (
    id: number,
    counterpartyId: number,
    body: CounterpartyCreateUpdate
  ) =>
    interceptRequest(
      updateCounterparty,
      {
        onSuccess: () =>
          showSuccessToastMessage(translate('messages.successfullyUpdated'))
      },
      id,
      counterpartyId,
      body
    );

  const deleteCounterParties = (id: number, counterpartyIds: number[]) =>
    interceptRequest(
      deleteCounterpartiesByIds,
      {
        onSuccess: () =>
          showSuccessToastMessage(translate('messages.successfullyDeleted'))
      },
      id,
      counterpartyIds
    );

  const getFilteredCounterpartiesByCompanyId = useCallback(
    (id: number, requestBody: CounterpartyQueryFilters) =>
      interceptRequest(
        getCounterpartiesByCompanyIdAndFilters,
        {},
        id,
        requestBody
      ),
    [getCounterpartiesByCompanyIdAndFilters]
  );

  const getCounterpartyFilters = (id: number) =>
    interceptRequest(getCounterpartyFiltersByCompanyIdAndType, {}, id);

  const getCounterpartyApprovalsByCompanyId = useCallback(
    (companyId: number) =>
      interceptRequest(getCounterpartyApprovals, {}, companyId),
    [getCounterpartyApprovals]
  );

  const updateCounterpartyApproval = useCallback(
    (
      companyId: number,
      counterpartyId: number,
      body: CounterpartyApprovalPatch
    ) =>
      interceptRequest(
        updateCounterpartyApprovalById,
        {},
        companyId,
        counterpartyId,
        body
      ),
    [updateCounterpartyApprovalById]
  );

  const checkIfCounterpartyExists = useCallback(
    (
      companyId: number,
      name: string,
      registrationNumber: string,
      vatNumber: string
    ) =>
      interceptRequest(
        checkCounterpartyExist,
        {},
        companyId,
        name,
        registrationNumber,
        vatNumber
      ),
    [checkCounterpartyExist]
  );

  const getCounterpartyById = useCallback(
    (companyId: number, id: number) =>
      interceptRequest(getCounterparty, {}, companyId, id),
    [getCounterparty]
  );

  return {
    createNewCounterParty,
    editCounterParty,
    deleteCounterParties,
    getFilteredCounterpartiesByCompanyId,
    getCounterpartyFilters,
    getCounterpartyApprovalsByCompanyId,
    updateCounterpartyApproval,
    checkIfCounterpartyExists,
    getCounterpartyById
  };
};
